import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../config';
import { useToast } from '../common/ToastProvider';
import { motion, AnimatePresence } from 'framer-motion';
import IMAGES from '../../data/images';
import { apiRequest } from '../../utils/api';
import { getAuthRedirectOrigin } from '../../utils/authRedirect';
import { lockBodyScroll, unlockBodyScroll } from '../../utils/scrollLock';
import Button from './ui/Button';

const SIGNIN_ATTEMPT_THRESHOLD = 3;
const SIGNIN_ATTEMPT_WINDOW_MS = 15 * 60 * 1000;
const OAUTH_ATTEMPT_THRESHOLD = 5;
const OAUTH_ATTEMPT_WINDOW_MS = 10 * 60 * 1000;
const DEFAULT_HCAPTCHA_SITE_KEY = '23943c08-5d3e-4854-9d42-cf8ebeaae67c';

const pruneAttempts = (attempts, windowMs) => {
  const windowStart = Date.now() - windowMs;
  return attempts.filter((timestamp) => timestamp > windowStart);
};

const OAUTH_PROVIDER_CONFIG = {
  google: {
    label: 'Google',
    buttonLabel: 'Sign in with Google',
    options: {
      scopes: 'openid email profile',
      queryParams: { prompt: 'select_account' },
    },
  },
  facebook: {
    label: 'Facebook',
    buttonLabel: 'Sign in with Facebook',
    options: {
      scopes: 'email public_profile',
    },
  },
};

function LoginModal({ isOpen, initialMode = 'signin', onClose, onLoginSuccess }) {
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaExpired, setCaptchaExpired] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [failedSignInAttempts, setFailedSignInAttempts] = useState([]);
  const [oauthAttempts, setOauthAttempts] = useState([]);
  const [isCaptchaReady, setIsCaptchaReady] = useState(false);
  const [captchaScriptReady, setCaptchaScriptReady] = useState(false);
  const [canResendVerification, setCanResendVerification] = useState(false);
  const captchaRef = useRef(null);
  const captchaWidgetIdRef = useRef(null);
  const modalRef = useRef(null);
  const emailInputRef = useRef(null);
  const lastFocusedElementRef = useRef(null);
  const { success, error: showError } = useToast();

  const runtimeCaptchaKey = typeof window !== 'undefined'
    ? (window.REACT_APP_HCAPTCHA_SITE_KEY || window.__ENV__?.REACT_APP_HCAPTCHA_SITE_KEY)
    : '';
  const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY
    || runtimeCaptchaKey
    || DEFAULT_HCAPTCHA_SITE_KEY;
  const recentSignInAttempts = pruneAttempts(failedSignInAttempts, SIGNIN_ATTEMPT_WINDOW_MS);
  const recentOauthAttempts = pruneAttempts(oauthAttempts, OAUTH_ATTEMPT_WINDOW_MS);
  const requiresCaptchaForEmail = mode === 'signup' || (mode === 'signin' && recentSignInAttempts.length >= SIGNIN_ATTEMPT_THRESHOLD);
  const requiresCaptchaForOAuth = recentOauthAttempts.length >= OAUTH_ATTEMPT_THRESHOLD || recentSignInAttempts.length >= SIGNIN_ATTEMPT_THRESHOLD;
  const shouldRenderCaptcha = mode !== 'forgot-password' && (requiresCaptchaForEmail || requiresCaptchaForOAuth);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (initialMode === 'signup' || initialMode === 'signin' || initialMode === 'forgot-password') {
      setMode(initialMode);
      return;
    }

    setMode('signin');
  }, [isOpen, initialMode]);

  useEffect(() => {
    setFailedSignInAttempts((prev) => pruneAttempts(prev, SIGNIN_ATTEMPT_WINDOW_MS));
    setOauthAttempts((prev) => pruneAttempts(prev, OAUTH_ATTEMPT_WINDOW_MS));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !shouldRenderCaptcha) {
      setCaptchaScriptReady(false);
      return;
    }

    if (window.hcaptcha) {
      setCaptchaScriptReady(true);
      return;
    }

    const intervalId = window.setInterval(() => {
      if (window.hcaptcha) {
        setCaptchaScriptReady(true);
        window.clearInterval(intervalId);
      }
    }, 250);

    return () => window.clearInterval(intervalId);
  }, [isOpen, shouldRenderCaptcha]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    lastFocusedElementRef.current = document.activeElement;

    const focusTimer = window.setTimeout(() => {
      emailInputRef.current?.focus();
    }, 0);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !modalRef.current) {
        return;
      }

      const focusableElements = modalRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const focusable = Array.from(focusableElements);

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const firstFocusable = focusable[0];
      const lastFocusable = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      if (lastFocusedElementRef.current && typeof lastFocusedElementRef.current.focus === 'function') {
        lastFocusedElementRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
    }

    return () => {
      if (isOpen) {
        unlockBodyScroll();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) return;

    if (window.hcaptcha && captchaWidgetIdRef.current !== null) {
      window.hcaptcha.remove(captchaWidgetIdRef.current);
    }

    captchaWidgetIdRef.current = null;
    setIsCaptchaReady(false);
    setCaptchaScriptReady(false);
    setCaptchaToken('');
    setCaptchaExpired(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setCaptchaToken('');
    setCaptchaExpired(false);

    if (!shouldRenderCaptcha) {
      if (window.hcaptcha && captchaWidgetIdRef.current !== null) {
        window.hcaptcha.remove(captchaWidgetIdRef.current);
        captchaWidgetIdRef.current = null;
      }
      setIsCaptchaReady(false);
      return;
    }

    if (!HCAPTCHA_SITE_KEY) {
      setIsCaptchaReady(false);
      return;
    }

    if (!captchaScriptReady || !window.hcaptcha || !captchaRef.current) {
      setIsCaptchaReady(false);
      return;
    }

    setIsCaptchaReady(true);

    if (captchaWidgetIdRef.current === null) {
      captchaWidgetIdRef.current = window.hcaptcha.render(captchaRef.current, {
        sitekey: HCAPTCHA_SITE_KEY,
        callback: (token) => {
          setCaptchaToken(token);
          setCaptchaExpired(false);
        },
        'expired-callback': () => {
          setCaptchaToken('');
          setCaptchaExpired(true);
        },
        'error-callback': () => {
          setCaptchaToken('');
          setCaptchaExpired(true);
        },
      });
      return;
    }

    window.hcaptcha.reset(captchaWidgetIdRef.current);
  }, [isOpen, shouldRenderCaptcha, HCAPTCHA_SITE_KEY, captchaScriptReady]);

  useEffect(() => {
    if (!isOpen) return;
    setCaptchaToken('');
    setCaptchaExpired(false);
    if (window.hcaptcha && captchaWidgetIdRef.current !== null && shouldRenderCaptcha) {
      window.hcaptcha.reset(captchaWidgetIdRef.current);
    }
  }, [mode, shouldRenderCaptcha, isOpen]);

  const verifyCaptcha = async (token, verificationMode) => {
    try {
      const data = await apiRequest('/hcaptcha-verify', {
        method: 'POST',
        body: { token, mode: verificationMode },
      });
      return data.success === true;
    } catch (err) {
      console.error('Captcha verification error:', err);
      return false;
    }
  };

  const handleOAuthLogin = async (provider) => {
    const providerConfig = OAUTH_PROVIDER_CONFIG[provider] || OAUTH_PROVIDER_CONFIG.google;
    const providerLabel = providerConfig.label;
    const now = Date.now();
    const nextOauthAttempts = [...pruneAttempts(oauthAttempts, OAUTH_ATTEMPT_WINDOW_MS), now];
    setOauthAttempts(nextOauthAttempts);
    const requiresCaptchaForThisOAuthAttempt =
      recentSignInAttempts.length >= SIGNIN_ATTEMPT_THRESHOLD ||
      nextOauthAttempts.length >= OAUTH_ATTEMPT_THRESHOLD;

    setLoading(true);

    try {
      if (requiresCaptchaForThisOAuthAttempt) {
        if (!HCAPTCHA_SITE_KEY) {
          showError('Captcha is not configured. Please contact support.');
          setLoading(false);
          return;
        }

        if (!captchaToken) {
          showError(`Please complete captcha verification before continuing with ${providerLabel}.`);
          setLoading(false);
          return;
        }

        const isCaptchaValid = await verifyCaptcha(captchaToken, 'oauth');
        if (!isCaptchaValid) {
          showError('Captcha verification failed. Please try again.');
          setCaptchaToken('');
          setLoading(false);
          return;
        }
      }

      const oauthResponse = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: getAuthRedirectOrigin(),
          ...providerConfig.options,
        },
      });

      const { error } = oauthResponse;

      if (error) {
        throw error;
      }

      setOauthAttempts([]);
      success(`Redirecting to ${providerLabel}...`);
    } catch (err) {
      showError(`We couldn\'t sign you in with ${providerLabel}. Please try again.`);
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!email) {
      showError('Please enter your email address first.');
      return;
    }

    setLoading(true);

    try {
      const redirectTo = `${getAuthRedirectOrigin()}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) throw error;

      success('Password reset link sent. Check your email inbox.');
      setMode('signin');
    } catch (err) {
      showError(err.message || 'Could not send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleResendVerification = async () => {
    if (!email) {
      showError('Enter your email address to resend verification.');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: getAuthRedirectOrigin(),
        },
      });

      if (error) {
        throw error;
      }

      success('Verification email sent. Please check your inbox.');
    } catch (err) {
      showError(err.message || 'Could not resend verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (requiresCaptchaForEmail) {
        if (!HCAPTCHA_SITE_KEY) {
          showError('Captcha is not configured. Please contact support.');
          setLoading(false);
          return;
        }

        if (!captchaToken) {
          showError('Please complete captcha verification.');
          setLoading(false);
          return;
        }

        const verificationMode = mode === 'signup' ? 'signup' : 'signin';
        const isCaptchaValid = await verifyCaptcha(captchaToken, verificationMode);
        if (!isCaptchaValid) {
          showError('Captcha verification failed. Please try again.');
          setCaptchaToken('');
          setLoading(false);
          return;
        }
      }

      if (mode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;

        setFailedSignInAttempts([]);
        setCanResendVerification(false);
        success('Signed in successfully!');
        onLoginSuccess();
        onClose();
        return;
      }

      if (password !== confirmPassword) {
        showError('Passwords do not match');
        setLoading(false);
        return;
      }

      if (password.length < 8) {
        showError('Use at least 8 characters for your password.');
        setLoading(false);
        return;
      }

      const normalizedFirstName = firstName.trim();
      const normalizedLastName = lastName.trim();

      if (!normalizedFirstName || !normalizedLastName) {
        showError('First and last name are required to create an account.');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: getAuthRedirectOrigin(),
          data: {
            first_name: normalizedFirstName,
            last_name: normalizedLastName,
            name: `${normalizedFirstName} ${normalizedLastName}`.trim(),
            full_name: `${normalizedFirstName} ${normalizedLastName}`.trim(),
          },
        },
      });

      if (error) throw error;

      if (data?.user?.identities?.length === 0) {
        showError('This email is already registered. Please sign in instead.');
        setMode('signin');
        return;
      }

      success('Account created! Check your email to verify, then sign in.');
      setMode('signin');
      setPassword('');
      setConfirmPassword('');
      setFirstName('');
      setLastName('');
      setCanResendVerification(true);
    } catch (err) {
      console.error('Auth error:', err);

      if (mode === 'signin') {
        setFailedSignInAttempts((prev) => [...prev, Date.now()]);
      }

      if (err.message.includes('Invalid login credentials')) {
        showError('Invalid email or password');
        setCanResendVerification(false);
      } else if (err.message.includes('Email not confirmed')) {
        showError('Please check your email and click the confirmation link');
        setCanResendVerification(true);
      } else {
        showError(err.message || 'Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setCanResendVerification(false);
  };

  const getPasswordStrengthLabel = (value) => {
    if (!value) {
      return null;
    }

    let score = 0;
    if (value.length >= 8) score += 1;
    if (/[A-Z]/.test(value)) score += 1;
    if (/[a-z]/.test(value)) score += 1;
    if (/\d/.test(value)) score += 1;
    if (/[^A-Za-z0-9]/.test(value)) score += 1;

    if (score <= 2) {
      return { label: 'Weak', className: 'text-red-500' };
    }

    if (score <= 4) {
      return { label: 'Okay', className: 'text-amber-500' };
    }

    return { label: 'Strong', className: 'text-emerald-600' };
  };

  const passwordStrength = mode === 'signup' ? getPasswordStrengthLabel(password) : null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className='fixed inset-0 bg-black/50 flex items-center justify-center z-modal p-3 sm:p-4'
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className='bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl overflow-hidden'
          onClick={(e) => e.stopPropagation()}
          role='dialog'
          aria-modal='true'
          aria-labelledby='auth-modal-title'
          ref={modalRef}
        >
          <div className='relative h-32 overflow-hidden'>
            <img src={IMAGES.loginBg} alt='' className='w-full h-full object-cover' />
            <div className='absolute inset-0 bg-gradient-to-b from-primary/30 to-white' />
          </div>

          <div className='p-6 -mt-8 relative'>
            <button
              onClick={onClose}
              className='absolute top-2 right-4 text-stone-400 hover:text-stone-600'
              aria-label='Close'
            >
              <span className='material-symbols-outlined text-2xl'>close</span>
            </button>

            <h2 id='auth-modal-title' className='text-2xl font-headline text-primary text-center mb-2'>
              {mode === 'signin' && 'Welcome Back'}
              {mode === 'signup' && 'Create Account'}
              {mode === 'forgot-password' && 'Reset Password'}
            </h2>
            <p className='text-stone-500 text-center mb-6'>
              {mode === 'signin' && 'Sign in to connect with other mums'}
              {mode === 'signup' && 'Join our community of mums'}
              {mode === 'forgot-password' && 'We will email a secure reset link'}
            </p>

            {mode !== 'forgot-password' && (
              <>
                <div className='flex flex-col gap-2 mb-6'>
                  <button
                    onClick={() => handleOAuthLogin('google')}
                    disabled={loading}
                    className='flex h-11 items-center justify-center gap-3 rounded-xl border border-stone-300 bg-white px-4 text-[15px] font-semibold text-stone-800 transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60'
                  >
                    <svg width='18' height='18' viewBox='0 0 18 18' aria-hidden='true'>
                      <path fill='#4285F4' d='M17.64 9.2045c0-.6382-.0573-1.2518-.1636-1.8409H9v3.4818h4.8436c-.2087 1.125-.8427 2.0782-1.7968 2.7155v2.2582h2.9086c1.7018-1.5664 2.6846-3.8755 2.6846-6.6146z' />
                      <path fill='#34A853' d='M9 18c2.43 0 4.4673-.8055 5.9564-2.1809l-2.9086-2.2582c-.8055.54-1.8368.8591-3.0478.8591-2.3441 0-4.3282-1.5827-5.0364-3.7091H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z' />
                      <path fill='#FBBC05' d='M3.9636 10.7109c-.18-.54-.2836-1.1168-.2836-1.7109s.1036-1.1709.2836-1.7109V4.9573H.9573C.3477 6.1727 0 7.5491 0 9s.3477 2.8273.9573 4.0427l3.0063-2.3318z' />
                      <path fill='#EA4335' d='M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.3459l2.5813-2.5814C13.4632.8918 11.4268 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9573l3.0063 2.3318C4.6718 5.1623 6.6559 3.5795 9 3.5795z' />
                    </svg>
                    {OAUTH_PROVIDER_CONFIG.google.buttonLabel}
                  </button>
                  <button
                    onClick={() => handleOAuthLogin('facebook')}
                    disabled={loading}
                    className='flex h-11 items-center justify-center gap-3 rounded-xl border border-stone-300 bg-white px-4 text-[15px] font-semibold text-[#1877F2] transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60'
                  >
                    <svg width='18' height='18' viewBox='0 0 24 24' aria-hidden='true'>
                      <path fill='#1877F2' d='M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073c0 6.027 4.388 11.023 10.125 11.927v-8.437H7.078v-3.49h3.047V9.413c0-3.007 1.792-4.67 4.533-4.67 1.313 0 2.686.235 2.686.235v2.953h-1.514c-1.492 0-1.956.927-1.956 1.875v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.096 24 18.1 24 12.073z' />
                    </svg>
                    {OAUTH_PROVIDER_CONFIG.facebook.buttonLabel}
                  </button>
                  {requiresCaptchaForOAuth && (
                    <p className='text-[11px] text-stone-500 text-center'>
                      Complete the security check below to continue with social sign in.
                    </p>
                  )}
                </div>

                <div className='flex items-center gap-4 mb-6'>
                  <div className='flex-1 h-px bg-stone-200' />
                  <span className='text-stone-400 text-sm'>or use email</span>
                  <div className='flex-1 h-px bg-stone-200' />
                </div>
              </>
            )}

            {mode === 'forgot-password' ? (
              <form onSubmit={handleForgotPassword} className='flex flex-col gap-4'>
                <div>
                  <label className='block text-sm font-medium text-stone-700 mb-1'>Email</label>
                  <input
                    type='email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder='your@email.com'
                    autoComplete='email'
                    className='w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-primary focus:ring-0 transition-colors'
                  />
                </div>

                <Button type='submit' disabled={loading} size="xl">
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <button
                  type='button'
                  onClick={() => setMode('signin')}
                  className='text-primary font-semibold hover:underline text-sm'
                >
                  Back to Sign In
                </button>
              </form>
            ) : (
              <form onSubmit={handleEmailSubmit} className='flex flex-col gap-4'>
                <div>
                  <label className='block text-sm font-medium text-stone-700 mb-1'>Email</label>
                  <input
                    type='email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder='your@email.com'
                    autoComplete='email'
                    ref={emailInputRef}
                    className='w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-primary focus:ring-0 transition-colors'
                  />
                </div>

                {mode === 'signup' && (
                  <>
                    <div>
                      <label className='block text-sm font-medium text-stone-700 mb-1'>First Name</label>
                      <input
                        type='text'
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        maxLength={80}
                        placeholder='First name'
                        autoComplete='given-name'
                        className='w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-primary focus:ring-0 transition-colors'
                      />
                    </div>

                    <div>
                      <label className='block text-sm font-medium text-stone-700 mb-1'>Last Name</label>
                      <input
                        type='text'
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        maxLength={80}
                        placeholder='Last name'
                        autoComplete='family-name'
                        className='w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-primary focus:ring-0 transition-colors'
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className='block text-sm font-medium text-stone-700 mb-1'>Password</label>
                  <div className='relative'>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      placeholder='At least 8 characters'
                      autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                      className='w-full px-4 py-3 pr-12 border-2 border-stone-200 rounded-xl focus:border-primary focus:ring-0 transition-colors'
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword((prev) => !prev)}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-primary'
                      aria-label='Toggle password visibility'
                    >
                      <span className='material-symbols-outlined text-[20px]'>
                        {showPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                  {mode === 'signup' && passwordStrength && (
                    <p className='mt-1 text-xs text-stone-500'>
                      Password strength: <span className={passwordStrength.className}>{passwordStrength.label}</span>
                    </p>
                  )}
                </div>

                {mode === 'signup' && (
                  <div>
                    <label className='block text-sm font-medium text-stone-700 mb-1'>Confirm Password</label>
                    <div className='relative'>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        placeholder='Re-enter password'
                        autoComplete='new-password'
                        className='w-full px-4 py-3 pr-12 border-2 border-stone-200 rounded-xl focus:border-primary focus:ring-0 transition-colors'
                      />
                      <button
                        type='button'
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className='absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-primary'
                        aria-label='Toggle confirm password visibility'
                      >
                        <span className='material-symbols-outlined text-[20px]'>
                          {showConfirmPassword ? 'visibility_off' : 'visibility'}
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {mode === 'signin' && (
                  <div className='text-right -mt-1'>
                    <button
                      type='button'
                      onClick={() => setMode('forgot-password')}
                      className='text-sm text-primary font-semibold hover:underline'
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                {mode === 'signin' && canResendVerification && (
                  <div className='text-right -mt-2'>
                    <button
                      type='button'
                      onClick={handleResendVerification}
                      disabled={loading}
                      className='text-sm text-primary font-semibold hover:underline disabled:opacity-60'
                    >
                      Resend verification email
                    </button>
                  </div>
                )}

                {shouldRenderCaptcha && (
                  <div className='flex flex-col items-center my-1'>
                    {!HCAPTCHA_SITE_KEY && (
                      <p className='text-red-500 text-sm text-center'>Captcha site key missing. Set `REACT_APP_HCAPTCHA_SITE_KEY`.</p>
                    )}
                    {HCAPTCHA_SITE_KEY && !captchaScriptReady && (
                      <p className='text-stone-500 text-sm text-center'>Preparing security check...</p>
                    )}
                    {HCAPTCHA_SITE_KEY && captchaScriptReady && !isCaptchaReady && (
                      <p className='text-stone-500 text-sm text-center'>Loading captcha challenge...</p>
                    )}
                    <div ref={captchaRef} />
                    {captchaExpired && (
                      <p className='text-red-500 text-sm text-center mt-1'>Captcha expired. Please complete it again.</p>
                    )}
                    {mode === 'signin' && (requiresCaptchaForEmail || requiresCaptchaForOAuth) && (
                      <p className='text-[11px] text-stone-400 mt-1'>
                        Extra verification is enabled after unusual sign-in activity.
                      </p>
                    )}
                    {mode === 'signup' && !requiresCaptchaForOAuth && (
                      <p className='text-[11px] text-stone-400 mt-1'>
                        Security verification is required to create an email account.
                      </p>
                    )}
                  </div>
                )}

                <Button type='submit' disabled={loading} size="xl">
                  {loading ? 'Please wait...' : mode === 'signin' ? 'Sign In' : 'Create Account'}
                </Button>
              </form>
            )}

            {mode === 'signup' && (
              <p className='mt-4 text-xs text-stone-500 text-center leading-relaxed'>
                By creating an account, you agree to our{' '}
                <a href='/terms' target='_blank' rel='noopener noreferrer' className='text-primary font-semibold hover:underline'>
                  Terms
                </a>{' '}
                and{' '}
                <a href='/privacy' target='_blank' rel='noopener noreferrer' className='text-primary font-semibold hover:underline'>
                  Privacy Policy
                </a>
                .
              </p>
            )}

            {mode !== 'forgot-password' && (
              <div className='mt-6 text-center text-stone-500'>
                {mode === 'signin' ? (
                  <>
                    Don&apos;t have an account?{' '}
                    <button onClick={toggleMode} className='text-primary font-semibold hover:underline'>
                      Sign Up
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button onClick={toggleMode} className='text-primary font-semibold hover:underline'>
                      Sign In
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default LoginModal;
